import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import {
  fetchUsers,
  fetchUserSummary,
  setUserEnabled,
  setUserLocked,
  resetUserPassword,
  updateUserRole,
  fetchAudit,
  fetchHealth,
  fetchJobs,
  triggerJob,
  setJobEnabled,
  fetchSettings,
  upsertSetting,
  exportUser,
  fetchIncidentReport,
  fetchRoles,
  updateRolePermissions,
} from "./AdminService";
import "./AdminBoard.css";

const ROLE_PERMISSIONS = {
  OWNER: ["*"],
  ADMIN: [
    "USER_READ",
    "USER_WRITE",
    "USER_DISABLE",
    "USER_LOCK",
    "USER_RESET_PASSWORD",
    "AUDIT_VIEW",
    "HEALTH_VIEW",
    "JOB_TRIGGER",
    "JOB_TOGGLE",
    "SETTINGS_READ",
    "SETTINGS_WRITE",
    "EXPORT_DATA",
    "INCIDENT_VIEW",
  ],
  SUPPORT: ["USER_READ", "AUDIT_VIEW", "INCIDENT_VIEW"],
  READ_ONLY: ["USER_READ", "AUDIT_VIEW", "HEALTH_VIEW"],
  USER: [],
};

const ALL_PERMISSIONS = [
  "USER_READ",
  "USER_WRITE",
  "USER_DISABLE",
  "USER_LOCK",
  "USER_RESET_PASSWORD",
  "USER_ROLE_MANAGE",
  "AUDIT_VIEW",
  "HEALTH_VIEW",
  "JOB_TRIGGER",
  "JOB_TOGGLE",
  "SETTINGS_READ",
  "SETTINGS_WRITE",
  "EXPORT_DATA",
  "INCIDENT_VIEW",
];

function hasPermission(role, permission) {
  if (!role) return false;
  if (role === "OWNER") return true;
  const permissions = ROLE_PERMISSIONS[role] || [];
  return permissions.includes(permission);
}

export default function AdminBoard() {
  const { auth } = useAuth();
  const role = auth?.role || "USER";
  const canUserRead = hasPermission(role, "USER_READ");
  const canUserDisable = hasPermission(role, "USER_DISABLE");
  const canUserLock = hasPermission(role, "USER_LOCK");
  const canUserReset = hasPermission(role, "USER_RESET_PASSWORD");
  const canJobToggle = hasPermission(role, "JOB_TOGGLE");
  const canJobTrigger = hasPermission(role, "JOB_TRIGGER");
  const canSettingsRead = hasPermission(role, "SETTINGS_READ");
  const canSettingsWrite = hasPermission(role, "SETTINGS_WRITE");
  const canAudit = hasPermission(role, "AUDIT_VIEW");
  const canHealth = hasPermission(role, "HEALTH_VIEW");
  const canIncident = hasPermission(role, "INCIDENT_VIEW");
  const canExport = hasPermission(role, "EXPORT_DATA");
  const isOwner = role === "OWNER";

  const [users, setUsers] = useState([]);
  const [userQuery, setUserQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSummary, setUserSummary] = useState(null);
  const [passwordReset, setPasswordReset] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [health, setHealth] = useState(null);
  const [audit, setAudit] = useState([]);
  const [settings, setSettings] = useState([]);
  const [incident, setIncident] = useState(null);
  const [roles, setRoles] = useState([]);
  const [rolePermissions, setRolePermissions] = useState(ALL_PERMISSIONS);
  const [status, setStatus] = useState("");

  const canAccessAdmin = useMemo(() => role !== "USER", [role]);

  useEffect(() => {
    if (canUserRead) {
      fetchUsers("", 50).then(setUsers);
    }
    if (canHealth) {
      fetchHealth().then(setHealth);
    }
    if (canJobToggle) {
      fetchJobs().then(setJobs);
    }
    if (canAudit) {
      fetchAudit({ limit: 50 }).then(setAudit);
    }
    if (canSettingsRead) {
      fetchSettings().then(setSettings);
    }
    if (canIncident) {
      fetchIncidentReport().then(setIncident);
    }
    if (isOwner) {
      fetchRoles().then((data) => {
        setRoles(data.roles || []);
        setRolePermissions(data.allPermissions || ALL_PERMISSIONS);
      });
    }
  }, [canUserRead, canHealth, canJobToggle, canAudit, canSettingsRead, canIncident, isOwner]);

  useEffect(() => {
    if (!selectedUser?.id || !canUserRead) return;
    fetchUserSummary(selectedUser.id).then(setUserSummary);
  }, [selectedUser, canUserRead]);

  if (!canAccessAdmin) {
    return (
      <div className="admin-page">
        <div className="admin-card">
          <h2>Admin</h2>
          <p>You do not have access to the admin console.</p>
        </div>
      </div>
    );
  }

  const handleSearch = async () => {
    const data = await fetchUsers(userQuery, 50);
    setUsers(data);
    setSelectedUser(null);
    setUserSummary(null);
  };

  const handleUserAction = async (action) => {
    if (!selectedUser?.id) return;
    setStatus("Updating user...");
    if (action === "disable") await setUserEnabled(selectedUser.id, false);
    if (action === "enable") await setUserEnabled(selectedUser.id, true);
    if (action === "lock") await setUserLocked(selectedUser.id, true);
    if (action === "unlock") await setUserLocked(selectedUser.id, false);
    if (action === "reset") {
      const reset = await resetUserPassword(selectedUser.id);
      setPasswordReset(reset);
    }
    if (action === "export" && canExport) {
      await exportUser(selectedUser.id);
    }
    const refreshed = await fetchUsers(userQuery, 50);
    setUsers(refreshed);
    if (selectedUser?.id) {
      const summary = await fetchUserSummary(selectedUser.id);
      setUserSummary(summary);
    }
    setStatus("Done.");
    setTimeout(() => setStatus(""), 1500);
  };

  const handleRoleChange = async (userId, roleValue) => {
    await updateUserRole(userId, roleValue);
    const refreshed = await fetchUsers(userQuery, 50);
    setUsers(refreshed);
    if (selectedUser?.id === userId) {
      setSelectedUser(refreshed.find((u) => u.id === userId));
    }
  };

  const handleJobToggle = async (jobKey, enabled) => {
    await setJobEnabled(jobKey, enabled);
    const refreshed = await fetchJobs();
    setJobs(refreshed);
  };

  const handleJobTrigger = async (jobKey) => {
    await triggerJob(jobKey);
    setStatus(`Triggered ${jobKey}`);
    setTimeout(() => setStatus(""), 1500);
  };

  const handleSettingSave = async (key, value) => {
    await upsertSetting(key, value);
    const refreshed = await fetchSettings();
    setSettings(refreshed);
  };

  const handleRolePermissionToggle = async (roleName, permission, enabled) => {
    const current = roles.find((r) => r.role === roleName);
    if (!current) return;
    const next = new Set(current.permissions || []);
    if (enabled) next.add(permission);
    else next.delete(permission);
    const updated = await updateRolePermissions(roleName, Array.from(next));
    setRoles((prev) => prev.map((r) => (r.role === roleName ? updated : r)));
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h2>Admin Console</h2>
          <p className="admin-subtitle">Role: {role}</p>
        </div>
        {status && <div className="admin-status">{status}</div>}
      </div>

      <div className="admin-grid">
        <section className="admin-card">
          <h3>Users</h3>
          <div className="admin-row">
            <input
              className="admin-input"
              placeholder="Search by username or email"
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
            />
            <button className="admin-btn" type="button" onClick={handleSearch}>
              Search
            </button>
          </div>

          <div className="admin-user-list">
            {users.map((user) => (
              <button
                key={user.id}
                className={`admin-user-item ${selectedUser?.id === user.id ? "active" : ""}`}
                type="button"
                onClick={() => {
                  setSelectedUser(user);
                  setPasswordReset(null);
                }}
              >
                <div>
                  <div className="admin-user-name">{user.username}</div>
                  <div className="admin-user-meta">{user.email?.[0] || "No email"}</div>
                </div>
                <div className="admin-user-role">{user.role || "USER"}</div>
              </button>
            ))}
          </div>
        </section>

        <section className="admin-card">
          <h3>User Details</h3>
          {!selectedUser ? (
            <p>Select a user to view details.</p>
          ) : (
            <>
              <div className="admin-user-detail">
                <div>
                  <div className="admin-user-name">{selectedUser.username}</div>
                  <div className="admin-user-meta">{selectedUser.email?.join(", ") || "No email"}</div>
                </div>
                <div className="admin-user-flags">
                  <span className={selectedUser.active ? "flag ok" : "flag warn"}>
                    {selectedUser.active ? "Active" : "Disabled"}
                  </span>
                  <span className={selectedUser.locked ? "flag warn" : "flag ok"}>
                    {selectedUser.locked ? "Locked" : "Unlocked"}
                  </span>
                </div>
              </div>

              {userSummary && (
                <div className="admin-metrics">
                  <div>Notes: {userSummary.noteCount}</div>
                  <div>Planner: {userSummary.plannerItemCount}</div>
                  <div>Files: {userSummary.fileCount}</div>
                  <div>Messages: {userSummary.incomingMessageCount}</div>
                  <div>On hold: {userSummary.incomingHoldCount}</div>
                  <div>Unparsed: {userSummary.incomingUnparsedCount}</div>
                </div>
              )}

              <div className="admin-row admin-actions">
                {canUserDisable && (
                  <>
                    <button className="admin-btn" type="button" onClick={() => handleUserAction("enable")}>
                      Enable
                    </button>
                    <button className="admin-btn danger" type="button" onClick={() => handleUserAction("disable")}>
                      Disable
                    </button>
                  </>
                )}
                {canUserLock && (
                  <>
                    <button className="admin-btn" type="button" onClick={() => handleUserAction("unlock")}>
                      Unlock
                    </button>
                    <button className="admin-btn warning" type="button" onClick={() => handleUserAction("lock")}>
                      Lock
                    </button>
                  </>
                )}
                {canUserReset && (
                  <button className="admin-btn" type="button" onClick={() => handleUserAction("reset")}>
                    Reset Password
                  </button>
                )}
                {canExport && (
                  <button className="admin-btn" type="button" onClick={() => handleUserAction("export")}>
                    Export Data
                  </button>
                )}
              </div>

              {isOwner && (
                <div className="admin-row admin-role">
                  <label>Role</label>
                  <select
                    value={selectedUser.role || "USER"}
                    onChange={(e) => handleRoleChange(selectedUser.id, e.target.value)}
                  >
                    {["OWNER", "ADMIN", "SUPPORT", "READ_ONLY", "USER"].map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {passwordReset && (
                <div className="admin-reset">
                  Temporary password: <strong>{passwordReset.tempPassword}</strong>
                </div>
              )}
            </>
          )}
        </section>

        {canHealth && (
          <section className="admin-card">
            <h3>System Health</h3>
            {health ? (
              <>
                <div className="admin-metrics">
                  <div>Uptime: {Math.round(health.uptimeMs / 1000)}s</div>
                </div>
                <div className="admin-health">
                  {(health.components || []).map((component) => (
                    <div key={component.name} className="admin-health-item">
                      <span>{component.name}</span>
                      <span className={`flag ${component.status === "ok" ? "ok" : "warn"}`}>
                        {component.status}
                      </span>
                      <span>{component.latencyMs ? `${component.latencyMs}ms` : ""}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p>No health data.</p>
            )}
          </section>
        )}

        {canJobToggle && (
          <section className="admin-card">
            <h3>Jobs</h3>
            <div className="admin-job-list">
              {jobs.map((job) => (
                <div key={job.jobKey} className="admin-job-item">
                  <div>
                    <div className="admin-job-name">{job.jobKey}</div>
                    <div className="admin-job-status">
                      {job.enabled ? "Enabled" : "Disabled"}
                      {job.lastStatus && ` • ${job.lastStatus}`}
                      {job.lastRunAt && ` • ${job.lastRunAt.slice(0, 19).replace("T", " ")}`}
                    </div>
                    {job.lastError && (
                      <div className="admin-job-error">{job.lastError}</div>
                    )}
                  </div>
                  <div className="admin-job-actions">
                    <button
                      className="admin-btn"
                      type="button"
                      onClick={() => handleJobToggle(job.jobKey, !job.enabled)}
                    >
                      {job.enabled ? "Disable" : "Enable"}
                    </button>
                    {canJobTrigger && (
                      <button
                        className="admin-btn"
                        type="button"
                        onClick={() => handleJobTrigger(job.jobKey)}
                      >
                        Trigger
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {canAudit && (
          <section className="admin-card">
            <h3>Audit Log</h3>
            <div className="admin-audit-list">
              {audit.map((event) => (
                <div key={event.id} className="admin-audit-item">
                  <div>
                    <div className="admin-audit-action">{event.action}</div>
                    <div className="admin-audit-meta">
                      {event.actor} • {event.targetType} {event.targetId}
                    </div>
                  </div>
                  <div className="admin-audit-date">{event.createdAt?.slice(0, 19)?.replace("T", " ")}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {canSettingsRead && (
          <section className="admin-card">
            <h3>Settings</h3>
            <div className="admin-settings">
              {settings.map((setting) => (
                <div key={setting.key} className="admin-setting-item">
                  <div className="admin-setting-key">{setting.key}</div>
                  {canSettingsWrite ? (
                    <input
                      className="admin-input"
                      defaultValue={setting.value || ""}
                      onBlur={(e) => handleSettingSave(setting.key, e.target.value)}
                    />
                  ) : (
                    <div className="admin-setting-value">{setting.value}</div>
                  )}
                </div>
              ))}
              {canSettingsWrite && (
                <div className="admin-setting-item">
                  <input className="admin-input" placeholder="New key" id="new-setting-key" />
                  <input className="admin-input" placeholder="New value" id="new-setting-value" />
                  <button
                    className="admin-btn"
                    type="button"
                    onClick={() => {
                      const key = document.getElementById("new-setting-key").value;
                      const value = document.getElementById("new-setting-value").value;
                      if (key) handleSettingSave(key, value);
                    }}
                  >
                    Save
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {canIncident && (
          <section className="admin-card">
            <h3>Integrity Checks</h3>
            {incident ? (
              <div className="admin-metrics">
                <div>Orphan Tags: {incident.orphanNoteTags}</div>
                <div>Hold Messages: {incident.incomingHoldCount}</div>
                <div>Unparsed Messages: {incident.incomingUnparsedCount}</div>
                <div>Deleted Files: {incident.deletedFileCount}</div>
              </div>
            ) : (
              <p>No incident report.</p>
            )}
          </section>
        )}

        {isOwner && (
          <section className="admin-card">
            <h3>Role Permissions</h3>
            <div className="admin-roles">
              {roles.map((roleConfig) => (
                <div key={roleConfig.role} className="admin-role-block">
                  <div className="admin-role-title">{roleConfig.role}</div>
                  <div className="admin-role-perms">
                    {rolePermissions.map((permission) => {
                      const enabled = roleConfig.permissions?.includes(permission);
                      const disabled = !roleConfig.editable;
                      return (
                        <label key={permission} className={`admin-perm ${disabled ? "disabled" : ""}`}>
                          <input
                            type="checkbox"
                            disabled={disabled}
                            checked={enabled}
                            onChange={(e) =>
                              handleRolePermissionToggle(roleConfig.role, permission, e.target.checked)
                            }
                          />
                          {permission}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
